// v2 goldsmith — a vaulted assay office: a stout plaster-and-stone strongroom
// under a verdigris barrel-vault roof, a hanging gold-coin sign over the door,
// and the trade out front — a stone crucible furnace with molten gold glowing in
// its cup, and a screw coin press (block, columns, head, screw and swing lever)
// with a freshly-struck coin on the die. Coin stacks, a gold bar and an
// iron-bound strongbox dress the yard. Ported 1:1 from age-of-mcp's
// v2/goldsmith.ts, using the library's shared `foundationSlab`, `scaffold` and
// `industry` (emberMouth) helpers.
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, industry, benchWork, smokePlume, firePulse, EFFECT_LOOP, type Part } from "../../lib";

const PLASTER = "#ddd0b6";  // office walls
const VAULT   = "#6f9a84";  // verdigris copper vault
const STONE   = "#6a6a6a";  // crucible furnace
const DOOR    = "#4a3018";
const DARK    = "#39444e";  // barred window
const GOLD    = "#e8c24a";  // coins / bar / sign
const IRON    = "#54565e";  // press frame
const IROND   = "#3a3a40";  // press block
const EMBER   = "#ffd24a";  // molten gold
const CHESTC  = "#4a3a28";  // strongbox

const BASE = 0.14;          // foundation slab top
const BX = -0.06, BZ = -0.10;        // office centre
const BW = 0.60, BD = 0.56;          // office body size
const FRONT = BZ + BD / 2;           // +z face — 0.18

// The office body, `h` of wall on the foundation.
function office(h: number): Part {
  return { geometry: new THREE.BoxGeometry(BW, h, BD), color: PLASTER,
    position: [BX, BASE + h / 2, BZ] };
}

// The verdigris barrel vault over the finished office, plus the front door,
// barred window and the hanging coin sign.
function vaultAndFront(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.32, 0.32, 0.60, 8), color: VAULT,
      position: [BX, BASE + 0.50, BZ], rotation: [Math.PI / 2, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.20, 0.34, 0.04), color: DOOR,
      position: [-0.20, BASE + 0.17, FRONT + 0.02] },
    { geometry: new THREE.BoxGeometry(0.14, 0.14, 0.03), color: DARK,
      position: [0.06, 0.50, FRONT + 0.02] },
    { geometry: new THREE.CylinderGeometry(0.055, 0.055, 0.02, 8), color: GOLD,
      position: [-0.20, 0.66, FRONT + 0.04], rotation: [Math.PI / 2, 0, 0] },
  ];
}

// The "melt is on" cue: the ember vent and the molten gold pulse while a thin
// fume wisp rises off the crucible cup.
const plume = smokePlume([0.30, 0.52, 0.12], { rise: 0.24, radius: 0.032, puffs: 2, drift: [0.03, 0.02] });

// The crucible furnace at the front-east corner: stone body, dark crucible
// cup with molten gold glowing in it, and a small ember vent on its face.
function crucibleFurnace(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.10, 0.13, 0.28, 7), color: STONE,
      position: [0.30, BASE + 0.14, 0.12] },
    { geometry: new THREE.CylinderGeometry(0.05, 0.035, 0.07, 6), color: IROND,
      position: [0.30, 0.455, 0.12] },
    { geometry: new THREE.CylinderGeometry(0.038, 0.038, 0.018, 6), color: EMBER,
      position: [0.30, 0.50, 0.12], name: "crucible-gold" },
    ...industry.emberMouth(0.30, 0.24, 0.235, 0.12, 0.09, "embers"),
    ...plume.parts,
  ];
}

// The screw coin press out front: anvil block, two columns, head, screw,
// swing lever and the freshly-struck coin on the die.
function coinPress(): Part[] {
  return [
    { geometry: new THREE.BoxGeometry(0.18, 0.12, 0.16), color: IROND,
      position: [0.07, BASE + 0.06, 0.32] },
    { geometry: new THREE.BoxGeometry(0.035, 0.24, 0.035), color: IRON,
      position: [0.01, 0.38, 0.32] },
    { geometry: new THREE.BoxGeometry(0.035, 0.24, 0.035), color: IRON,
      position: [0.13, 0.38, 0.32] },
    { geometry: new THREE.BoxGeometry(0.17, 0.06, 0.10), color: IRON,
      position: [0.07, 0.53, 0.32] },
    { geometry: new THREE.CylinderGeometry(0.018, 0.018, 0.10, 5), color: IROND,
      position: [0.07, 0.45, 0.32] },
    { geometry: new THREE.BoxGeometry(0.34, 0.025, 0.025), color: IRON,
      position: [0.07, 0.57, 0.32], rotation: [0, 0.6, 0] },
    { geometry: new THREE.CylinderGeometry(0.03, 0.03, 0.015, 8), color: GOLD,
      position: [0.07, 0.27, 0.32] },
  ];
}

// Minted coin stacks, a gold bar and the iron-bound strongbox.
function riches(): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.045, 0.045, 0.035, 8), color: GOLD,
      position: [0.33, BASE + 0.018, 0.38] },
    { geometry: new THREE.CylinderGeometry(0.045, 0.045, 0.035, 8), color: GOLD,
      position: [0.33, BASE + 0.053, 0.38] },
    { geometry: new THREE.CylinderGeometry(0.045, 0.045, 0.035, 8), color: GOLD,
      position: [0.25, BASE + 0.018, 0.41] },
    { geometry: new THREE.BoxGeometry(0.10, 0.04, 0.05), color: GOLD,
      position: [0.30, BASE + 0.02, 0.30], rotation: [0, 0.3, 0] },
    { geometry: new THREE.BoxGeometry(0.16, 0.13, 0.12), color: CHESTC,
      position: [-0.34, BASE + 0.065, 0.30], rotation: [0, -0.15, 0] },
    { geometry: new THREE.BoxGeometry(0.17, 0.03, 0.13), color: GOLD,
      position: [-0.34, BASE + 0.10, 0.30], rotation: [0, -0.15, 0] },
  ];
}

function stage(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.84)];
  if (s === 1) return [foundationSlab(0.84), office(0.26), ...scaffold(0.82, 0.6)];
  if (s === 2) return [foundationSlab(0.84), office(0.50), crucibleFurnace()[0]!, ...scaffold(0.82, 1.0)];
  return [
    foundationSlab(0.84),
    office(0.50),
    ...vaultAndFront(),
    ...crucibleFurnace(),
    ...coinPress(),
    ...riches(),
  ];
}

export const goldsmithV2 = defineModel({
  id: "goldsmith-v2",
  tiles: { w: 2, h: 2 },
  batchId: "fable",
  name: "Goldsmith (v2)",
  tags: ["building","goldsmith","low-poly","age-of-mcp","fable","animated"],
  summary: "Low-poly goldsmith.",
  description: "A goldsmith's forge that refines raw gold into minted coins.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => stage(0) },
    { id: "stage-1", label: "Lower", parts: () => stage(1) },
    { id: "stage-2", label: "Upper", parts: () => stage(2) },
    { id: "complete", label: "Complete", parts: () => stage(3) },
  ],
  animations: [
    {
      id: "active",
      label: "Melt on",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [
        ...plume.tracks,
        firePulse("embers", [0.30, 0.24, 0.24]),
        firePulse("crucible-gold", [0.30, 0.50, 0.12], { cycles: 3, amount: 1.25 }),
      ],
    },
  ],
  stations: [
    { id: "bench", position: [0.07, 0.14, 0.52], facing: Math.PI },
  ],
  interactions: [
    { id: "work", label: "At the bench", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
